import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Factory, LogOut, Clock, Send, FileUp, FileCheck2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../admin/AuthContext";

function errText(detail, fallback) {
  return typeof detail === "string" ? detail : fallback;
}

export function SupplierAuth() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", products: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.role === "supplier" ? "/supplier" : user.role === "customer" ? "/portal" : "/admin"} replace />;
  const h = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await api.post("/auth/supplier-register", form).then(() => login(form.email, form.password));
      navigate("/supplier");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(errText(d, "Something went wrong. Please try again."));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-white font-[Poppins] font-bold text-[22px] tracking-tight">PATRICK GLOBAL</Link>
          <div className="text-[11px] tracking-[0.2em] text-[var(--pg-accent)] uppercase font-semibold mt-1">Supplier Portal</div>
        </div>
        <form data-testid="supplier-auth-form" onSubmit={submit} className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="w-12 h-12 rounded-xl bg-[var(--pg-navy)] text-white flex items-center justify-center mb-5"><Factory size={22} /></div>
          <div className="flex gap-2 mb-5">
            <button type="button" data-testid="supplier-mode-login" onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2.5 rounded-lg font-[Poppins] font-semibold text-[13.5px] ${mode === "login" ? "bg-[var(--pg-navy)] text-white" : "bg-slate-100 text-slate-500"}`}>Sign In</button>
            <button type="button" data-testid="supplier-mode-register" onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2.5 rounded-lg font-[Poppins] font-semibold text-[13.5px] ${mode === "register" ? "bg-[var(--pg-navy)] text-white" : "bg-slate-100 text-slate-500"}`}>Register as Supplier</button>
          </div>
          {mode === "register" && (
            <>
              <div className="mb-4"><label className="pg-label">Company Name *</label><input data-testid="supplier-reg-company-input" className="pg-input" value={form.company_name} onChange={h("company_name")} required /></div>
              <div className="mb-4 grid grid-cols-2 gap-3">
                <div><label className="pg-label">Contact Name *</label><input data-testid="supplier-reg-contact-input" className="pg-input" value={form.contact_name} onChange={h("contact_name")} required /></div>
                <div><label className="pg-label">Phone</label><input data-testid="supplier-reg-phone-input" className="pg-input" value={form.phone} onChange={h("phone")} /></div>
              </div>
              <div className="mb-4"><label className="pg-label">Products You Supply</label><input data-testid="supplier-reg-products-input" className="pg-input" value={form.products} onChange={h("products")} placeholder="e.g. valves, PPE, IT hardware" /></div>
            </>
          )}
          <div className="mb-4"><label className="pg-label">Email *</label><input data-testid="supplier-email-input" type="email" className="pg-input" value={form.email} onChange={h("email")} required autoComplete="username" /></div>
          <div><label className="pg-label">Password * {mode === "register" && <span className="text-slate-400 font-normal">(min 8 characters)</span>}</label>
            <input data-testid="supplier-password-input" type="password" className="pg-input" value={form.password} onChange={h("password")} required minLength={mode === "register" ? 8 : undefined} /></div>
          {error && <p data-testid="supplier-auth-error" className="mt-4 text-[13.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="supplier-auth-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center mt-6 disabled:opacity-70">
            {loading ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Sign In" : "Register"}
          </button>
          {mode === "register" && <p className="mt-4 text-[12px] text-slate-500 text-center">New supplier accounts are reviewed by Patrick Global before RFQ access is granted.</p>}
        </form>
      </div>
    </div>
  );
}

export default function SupplierPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [denied, setDenied] = useState(false);
  const [wrongRole, setWrongRole] = useState(false);

  const load = useCallback(() => {
    api.get("/supplier/me").then((r) => {
      setMe(r.data);
      if (r.data.approved) api.get("/supplier/rfqs").then((x) => setRfqs(x.data)).catch(() => {});
    }).catch((err) => {
      if (err?.response?.status === 403) setWrongRole(true);
      else setDenied(true);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied) return <Navigate to="/supplier/login" replace />;
  if (wrongRole) {
    const homePath = user?.role === "customer" ? "/portal" : "/admin";
    return (
      <div className="min-h-screen hero-bg flex items-center justify-center px-4 py-16">
        <div data-testid="supplier-not-supplier-panel" className="w-full max-w-md bg-white rounded-2xl p-10 shadow-2xl text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 text-amber-600 flex items-center justify-center"><Factory size={26} /></div>
          <h2 className="mt-5 font-[Poppins] font-bold text-[20px] !text-[var(--pg-navy)]">This account is not a supplier account</h2>
          <p className="mt-3 text-[14.5px] text-slate-500">You're signed in as {user?.email || "a non-supplier user"}. The Supplier Portal is only for approved supplier accounts.</p>
          <div className="mt-6 flex flex-col gap-3">
            <Link data-testid="supplier-goto-my-portal" to={homePath} className="btn-primary justify-center">Go to my portal</Link>
            <button data-testid="supplier-signout-switch" onClick={async () => { await logout(); navigate("/supplier/login"); }}
              className="text-[13.5px] font-[Poppins] font-semibold text-slate-500 hover:text-[var(--pg-navy)]">Sign out &amp; use a supplier account</button>
          </div>
        </div>
      </div>
    );
  }
  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--pg-accent)]" size={30} /></div>;

  return (
    <div data-testid="supplier-portal-page" className="min-h-screen bg-[var(--pg-bg)]">
      <section className="pt-[120px] pb-10 hero-bg">
        <div className="pg-container flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="eyebrow" style={{ color: "#7EC4FF" }}>Supplier Portal</span>
            <h1 className="mt-3 text-[30px] md:text-[38px] font-extrabold leading-[1.06] text-white">{me.company_name}</h1>
            <p className="mt-2 text-white/75 text-[15px]">Respond to Patrick Global quote requests directly.</p>
          </div>
          <button data-testid="supplier-signout-button" onClick={async () => { await logout(); navigate("/"); }}
            className="inline-flex items-center gap-2 text-[13px] font-[Poppins] font-semibold text-white/80 hover:text-white border border-white/30 rounded-full px-4 py-2.5">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </section>

      <div className="pg-container py-10">
        {!me.approved ? (
          <div className="max-w-xl mx-auto space-y-5">
            <div data-testid="supplier-pending-approval" className="bg-white border border-amber-200 rounded-2xl p-10 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 text-amber-600 flex items-center justify-center"><Clock size={26} /></div>
              <h2 className="mt-5 font-[Poppins] font-bold text-[20px] text-[var(--pg-navy)]">Account pending approval</h2>
              <p className="mt-3 text-[14.5px] text-slate-500">Your supplier account (status: {me.status.replace(/_/g, " ")}) is being reviewed by the Patrick Global procurement team. You'll receive RFQs here once approved. Questions? Call 281-901-9914.</p>
            </div>
            <PendingDocsUpload />
          </div>
        ) : (
          <div className="space-y-4" data-testid="supplier-rfq-list">
            {rfqs.length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400" data-testid="supplier-rfqs-empty">No quote requests assigned yet. Patrick Global will send RFQs matching your products.</div>}
            {rfqs.map((r) => <SupplierRFQCard key={r.id} srfq={r} onChange={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function PendingDocsUpload() {
  const [docs, setDocs] = useState(null);
  const inputRef = useRef(null);
  const load = useCallback(() => {
    api.get("/supplier/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/supplier/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded — thank you!`);
      load();
    } catch (err) { toast.error(errText(err?.response?.data?.detail, "Upload failed")); }
    e.target.value = "";
  };
  return (
    <div data-testid="supplier-pending-docs" className="bg-white border border-slate-200 rounded-2xl p-8">
      <h3 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)] flex items-center gap-2"><FileUp size={17} /> Speed up your review</h3>
      <p className="mt-2 text-[13.5px] text-slate-500">Upload your <strong>W-9</strong>, <strong>insurance certificate</strong>, capability statement or other supporting documents. Our team reviews them with your application.</p>
      <div className="mt-4 space-y-2">
        {docs === null && <p className="text-[13px] text-slate-400">Loading documents...</p>}
        {docs !== null && docs.length === 0 && <p className="text-[13px] text-slate-400" data-testid="supplier-docs-none">No documents uploaded yet.</p>}
        {(docs || []).map((d) => (
          <div key={d.id} data-testid={`supplier-own-doc-${d.id}`} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-[13px]">
            <FileCheck2 size={14} className="text-green-600 flex-shrink-0" />
            <span className="truncate text-[var(--pg-navy)] font-medium">{d.original_filename}</span>
            <span className="ml-auto text-[11.5px] text-slate-400 flex-shrink-0">{(d.created_at || "").slice(0, 10)}</span>
          </div>
        ))}
      </div>
      <input ref={inputRef} type="file" className="hidden" onChange={upload} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.zip,.txt" />
      <button data-testid="supplier-upload-doc-button" onClick={() => inputRef.current?.click()}
        className="mt-4 btn-primary !py-2.5 !px-5 text-[13px]"><FileUp size={14} /> Upload Document</button>
      <p className="mt-2 text-[11.5px] text-slate-400">PDF, Word, Excel, images or ZIP · max 10MB per file</p>
    </div>
  );
}

function SupplierRFQCard({ srfq, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ unit_price: "", quantity: srfq.quantity && !isNaN(parseFloat(srfq.quantity)) ? srfq.quantity : "", lead_time_days: "", availability: "", shipping_cost: "0", freight: "", insurance: "", duties: "", handling: "", other_costs: "", warranty: "", expires_at: "", terms: "", notes: "", part_number: "", manufacturer: "", payment_terms: "", supplier_quote_number: "" });
  const [busy, setBusy] = useState(false);
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async () => {
    if (!f.unit_price || !f.quantity) { toast.error("Unit price and quantity are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/supplier/rfqs/${srfq.id}/quote`, {
        unit_price: parseFloat(f.unit_price), quantity: parseFloat(f.quantity),
        lead_time_days: parseInt(f.lead_time_days || 0, 10), shipping_cost: parseFloat(f.shipping_cost || 0),
        freight: parseFloat(f.freight || 0), insurance: parseFloat(f.insurance || 0),
        duties: parseFloat(f.duties || 0), handling: parseFloat(f.handling || 0), other_costs: parseFloat(f.other_costs || 0),
        availability: f.availability,
        warranty: f.warranty, expires_at: f.expires_at, terms: f.terms, notes: f.notes,
        part_number: f.part_number, manufacturer: f.manufacturer,
        payment_terms: f.payment_terms, supplier_quote_number: f.supplier_quote_number,
      });
      toast.success(data.message); onChange();
    } catch (err) { toast.error(errText(err?.response?.data?.detail, "Submission failed")); }
    finally { setBusy(false); }
  };

  const decline = async () => {
    if (!window.confirm(`Decline ${srfq.rfq_number}? Patrick Global will be notified and this cannot be undone.`)) return;
    try { await api.post(`/supplier/rfqs/${srfq.id}/decline`, { reason: "" }); toast.success("RFQ declined"); onChange(); }
    catch { toast.error("Action failed"); }
  };

  const badge = { PENDING: "bg-blue-100 text-blue-700", VIEWED: "bg-sky-100 text-sky-700", QUOTED: "bg-green-100 text-green-700", DECLINED: "bg-slate-200 text-slate-500", EXPIRED: "bg-amber-100 text-amber-700", CANCELLED: "bg-slate-200 text-slate-500" }[srfq.status] || "bg-slate-100 text-slate-600";
  const canRespond = ["PENDING", "VIEWED"].includes(srfq.status);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`supplier-rfq-${srfq.rfq_number}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">{srfq.rfq_number}</span>
          <span className={`ml-3 px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${badge}`}>{srfq.status}</span>
          <div className="mt-1.5 text-[13.5px] text-slate-600">{srfq.product_service} · Qty {srfq.quantity} {srfq.unit}</div>
          {srfq.quote_deadline && <div className="text-[12.5px] text-amber-600 font-medium mt-0.5">Quote deadline: {srfq.quote_deadline}</div>}
        </div>
        {canRespond && (
          <div className="flex gap-2">
            <button data-testid={`supplier-rfq-quote-toggle-${srfq.id}`} onClick={() => setOpen(!open)} className="btn-primary !py-2.5 !px-4 text-[13px]"><Send size={14} /> Submit Quote</button>
            <button data-testid={`supplier-rfq-decline-${srfq.id}`} onClick={decline} className="text-[13px] font-[Poppins] font-semibold text-slate-500 hover:text-red-600 px-3">Decline</button>
          </div>
        )}
      </div>
      {(srfq.specifications || srfq.message) && (
        <div className="mt-3 bg-slate-50 border-l-4 border-[var(--pg-accent)] rounded-md p-3 text-[13px] text-[var(--pg-navy)] whitespace-pre-wrap">
          {srfq.specifications}{srfq.message && `\nNote from Patrick Global: ${srfq.message}`}
        </div>
      )}
      {open && canRespond && (
        <div className="mt-4 grid sm:grid-cols-4 gap-3 bg-[var(--pg-bg)] rounded-xl p-4" data-testid="supplier-quote-form">
          <div><label className="pg-label">Unit Price ($) *</label><input data-testid="sp-unit-price-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.unit_price} onChange={h("unit_price")} /></div>
          <div><label className="pg-label">Qty Available *</label><input data-testid="sp-quantity-input" type="number" className="pg-input !py-2.5" value={f.quantity} onChange={h("quantity")} /></div>
          <div><label className="pg-label">Lead Time (days)</label><input data-testid="sp-lead-time-input" type="number" className="pg-input !py-2.5" value={f.lead_time_days} onChange={h("lead_time_days")} /></div>
          <div><label className="pg-label">Shipping ($)</label><input data-testid="sp-shipping-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.shipping_cost} onChange={h("shipping_cost")} /></div>
          <div><label className="pg-label">Freight ($)</label><input data-testid="sp-freight-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.freight} onChange={h("freight")} /></div>
          <div><label className="pg-label">Insurance ($)</label><input data-testid="sp-insurance-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.insurance} onChange={h("insurance")} /></div>
          <div><label className="pg-label">Duties ($)</label><input data-testid="sp-duties-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.duties} onChange={h("duties")} /></div>
          <div><label className="pg-label">Handling ($)</label><input data-testid="sp-handling-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.handling} onChange={h("handling")} /></div>
          <div><label className="pg-label">Other Costs ($)</label><input data-testid="sp-other-costs-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.other_costs} onChange={h("other_costs")} /></div>
          <div><label className="pg-label">Availability</label><input data-testid="sp-availability-input" placeholder="e.g. In stock" className="pg-input !py-2.5" value={f.availability} onChange={h("availability")} /></div>
          <div><label className="pg-label">Part Number</label><input data-testid="sp-part-number-input" className="pg-input !py-2.5" value={f.part_number} onChange={h("part_number")} /></div>
          <div><label className="pg-label">Manufacturer</label><input data-testid="sp-manufacturer-input" className="pg-input !py-2.5" value={f.manufacturer} onChange={h("manufacturer")} /></div>
          <div><label className="pg-label">Payment Terms</label><input data-testid="sp-payment-terms-input" placeholder="e.g. Net 30" className="pg-input !py-2.5" value={f.payment_terms} onChange={h("payment_terms")} /></div>
          <div><label className="pg-label">Your Quote #</label><input data-testid="sp-supplier-quote-number-input" className="pg-input !py-2.5" value={f.supplier_quote_number} onChange={h("supplier_quote_number")} /></div>
          <div><label className="pg-label">Warranty</label><input data-testid="sp-warranty-input" className="pg-input !py-2.5" value={f.warranty} onChange={h("warranty")} /></div>
          <div><label className="pg-label">Quote Expires</label><input data-testid="sp-expires-input" type="date" className="pg-input !py-2.5" value={f.expires_at} onChange={h("expires_at")} /></div>
          <div className="sm:col-span-2"><label className="pg-label">Terms / Notes</label><input data-testid="sp-terms-input" className="pg-input !py-2.5" value={f.terms} onChange={h("terms")} /></div>
          <div className="sm:col-span-4">
            <button data-testid="sp-submit-quote-button" onClick={submit} disabled={busy} className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Submit Quote to Patrick Global"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
